import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import styles from './index.module.scss'
import API, { commonApiInvoker } from '@api/api'
import { Button, Divider, Empty, Image, Input, message, Select, Space, Spin, Checkbox, Radio } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import InfiniteScroll from 'react-infinite-scroll-component'
import Utils from "@utils/utils"
import ImagePreviewWithLabel from '@view/compoents/ImagePreviewWithLabel';
import eventBus from '@utils/eventBus';
import { useLocation } from 'react-router-dom'
import { flushSync } from 'react-dom'
import PSAIModal from "@PSAIComponents/PSAIModal";
import JSZip from 'jszip'
import axios from 'axios'
import { saveAs } from "file-saver";
import DeliveryDetailActions from "../DeliveryDetailActions";
import DeliveryItem from "../DeliveryItem";

const DeliveryDetail = forwardRef(({ version, searchText, onDownload }, ref) => {
    useImperativeHandle(ref, () => ({
        updateForce: () => updateForce({}),
    }))

    const pageRef = useRef(1)
    const pageSizeRef = useRef(20)
    const [dataSource, setDataSource] = useState([])
    const [total, setTotal] = useState(0)
    const [deliveryInfo, setDeliveryInfo] = useState()
    const [force, updateForce] = useState()
    // 批量操作
    const [allRows, setAllRows] = useState([])
    const [isMultiSelect, setIsMultiSelect] = useState(false)
    const [selectRowKeys, setSelectRowKeys] = useState([])

    const getDeliveryInfoByVersion = useCallback(() => {
        if (!version) return
        commonApiInvoker(API.getDeliveryInfoByVersion({
            deliveryVersion: version,
        })).then(res => {
            setDeliveryInfo(res)
        })
    }, [version])
    const refreshData = useCallback(() => {
        console.log('==refreshData==')
        if (!version) return
        pageRef.current = 1
        const params = new URLSearchParams()
        params.append('version', version)
        params.append('pageIndex', pageRef.current)
        params.append('pageSize', pageSizeRef.current)
        // 因为搜索货号时，有数据的没有排在前面，可能不会触发下拉分页
        // 所以这里需要查出全部数据
        if (searchText) {
            params.append('skuCode', searchText)
            return commonApiInvoker(API.listDeliveryByVersion(params), false, true).then(res => {
                const items = res.items || []
                const total = res.total || 0
                if (items.length < total) {
                    const params = new URLSearchParams()
                    params.append('version', version)
                    params.append('pageIndex', pageRef.current)
                    params.append('pageSize', total)
                    params.append('skuCode', searchText)
                    return commonApiInvoker(API.listDeliveryByVersion(params), false, true).then(res => {
                        const items = res.items || []
                        const total = res.total || 0
                        setDataSource(items)
                        setTotal(total)
                        return total
                    })
                } else {
                    setDataSource(items)
                    setTotal(total)
                    return total
                }                
            }).catch(err => {
                console.error(err)
            })
        } else {
            return commonApiInvoker(API.listDeliveryByVersion(params), false, true).then(res => {
                const items = res.items || []
                const total = res.total || 0
                setDataSource(items)
                setTotal(total)
                return total
            }).catch(err => {
                console.error(err)
            })
        }
    }, [version, searchText])
    const fetchData = useCallback(() => {
        console.log('==fetchData==')
        pageRef.current += 1
        const params = new URLSearchParams()
        params.append('version', version)
        params.append('pageIndex', pageRef.current)
        params.append('pageSize', pageSizeRef.current)
        if (searchText) params.append('skuCode', searchText)
        commonApiInvoker(API.listDeliveryByVersion(params)).then(res => {
            const items = res.items || []
            const total = res.total || 0
            setDataSource(pre => [...pre, ...items])
            setTotal(total)
        })
    }, [version, searchText])
    const getAllRows = useCallback(async (total) => {
        try {
            if (!version) return
            if (!total) return
            const params = new URLSearchParams()
            params.append('version', version)
            params.append('pageIndex', 1)
            params.append('pageSize', total)
            if (searchText) params.append('skuCode', searchText)
            const res = await commonApiInvoker(API.listDeliveryByVersion(params), false, true)
            const dataSource = res.items || []
            setAllRows(dataSource.flatMap(item => item.skuDeliveryList))
        } catch (err) {
            console.error(err)
        }
    }, [version, searchText])
    useEffect(() => {
        refreshData().then(getAllRows)
        getDeliveryInfoByVersion()
    }, [refreshData, getDeliveryInfoByVersion, getAllRows, force])

    const hasMore = useMemo(() => {
        return dataSource.length < total
    }, [total, dataSource])

    const previewRef = useRef()
    const handlePreview = useCallback(async ({imageId, skuId, spuId}) => {
        try {
            const params = new URLSearchParams()
            params.append('version', version)
            params.append('pageIndex', 1)
            params.append('pageSize', total)
            if (searchText) params.append('skuCode', searchText)
            const res = await commonApiInvoker(API.listDeliveryByVersion(params), false, true)
            const dataSource = res.items || []
            const spuList = dataSource.map(item => {
                const spuId = item.spuCode
                const skuList = item.skuDeliveryList.map(item2 => {
                    const skuId = item2.skuCode || item2.skuNumber // 没填商品货号，使用skuNumber作为skuid               
                    let skuImages = item2.skuImages?.filter(item => !!item) || []
                    // if (skuImages.length == 0) {
                    //     skuImages = [...new Set(item2.deliveryImages.filter(item => item.skuImage).map(item => item.skuImage))]
                    // }
                    const skuImage = skuImages[0]
                    const imageList = item2.deliveryImages.map(item3 => {
                        const imageId = item3.imageId
                        const userPrevImage = item3.userPrevImage
                        const status = item3.status // 1已交付 2 已交付通过 3 已交付不通过
                        const refine = item3.refine // 已微调
                        let annotations, prevImage
                        if ((status == 0 || status == 1) && refine) { // 微调图，标注显示在质检图上
                            annotations = null
                            prevImage = userPrevImage
                        } else if (status == 2) { // 交付通过，不显示标注
                            annotations = null
                            prevImage = null
                        } else {
                            annotations = userPrevImage?.annotations
                            prevImage = null
                        }
                        return {
                            spuId,
                            skuId,
                            imageId,
                            deliveryId: item2.id,
                            deliveryStatus: item3.status, // 1已交付 2 已交付通过 3 已交付不通过 -1已取消
                            inputImage: (item2.deliveryTypes && !item2.deliveryTypes.includes("origin")) ? null : item3.skuImage, // 源素材
                            matchClothImage: (item2.deliveryTypes && !item2.deliveryTypes.includes("match")) ? null : item3.matchClothImage, // 搭配图
                            refImage: (item2.deliveryTypes && !item2.deliveryTypes.includes("reference")) ? null : item3.refImage, // 参考图
                            output: (item2.deliveryTypes && !item2.deliveryTypes.includes("delivery")) ? null : item3.url, // 交付图
                            annotations: annotations, // 结果图上的标注
                            prevImage: prevImage, // 用户质检图上的标注
                            deliverySubmit: !!item3.deliverySubmit, // 是否已反馈 
                        }
                    })
                    return {
                        spuId,
                        skuId,
                        skuImage,
                        imageList,
                    }
                })
                return {
                    spuId,
                    skuList,
                }
            })

            previewRef.current.handlePreview({
                current: {imageId, skuId, spuId},
                dataSource: spuList
            })
        } catch (err) {
            console.error(err)
        }
    }, [total, version, searchText])
    const onPreviewClose = useCallback(() => {
        if (!version) return
        updateForce({})
        eventBus.emit('updatePower')
    }, [version])
    const handleLabel = useCallback((id, labels) => {
        // console.log('标注。。。', id, labels)
    }, [])

    const handleSubmit = useCallback((deliveryIds, callback) => {
        PSAIModal.confirm({
            content: (
                <div style={{textAlign:"center"}}>
                    提交后，当前将不可再修改审核意见 <br />
                    是否提交全部反馈？
                </div>
            ),
            okText: '提交',
            cancelText: '取消',
            onOk () {
                return commonApiInvoker(API.merchantFeedback({
                    deliveryIds
                }), false, true).then(res => {
                    updateForce({})
                    eventBus.emit('updatePower')
                    message.success('提交反馈成功')
                    if (callback) callback()
                })
            }
        })
    }, [])

    const onSelectAll = useCallback(() => {
        setSelectRowKeys(allRows.map(item2 => item2.id))
    }, [allRows])
    const onMultiSelect = useCallback((isSelect) => {
        setIsMultiSelect(isSelect)
        setSelectRowKeys([])
    }, [])
    const onMultiDownload = useCallback(() => {
        console.log('多选下载', selectRowKeys)
        if (selectRowKeys.length == 0) return message.warning('请选择要下载的批次')
        const selectRows = dataSource.flatMap(item => item.skuDeliveryList).filter(item => selectRowKeys.includes(item.id))
        onDownload(selectRowKeys, selectRows, () => {
            setSelectRowKeys([])
            setIsMultiSelect(false)
        })
    }, [selectRowKeys, onDownload, dataSource])
    const onMultiFeedback = useCallback(() => {
        if (selectRowKeys.length == 0) return message.warning('请选择要提交反馈的批次')
        // 筛选出可以提交反馈的数据
        const checkAllImageSubmited = (item) => {
            const deliveryImages = item.deliveryImages || []
            return deliveryImages.every(item => !!item.deliverySubmit)
        }
        const checkAllImageMarked = (item) => {
            const deliveryImages = item.deliveryImages || []
            return deliveryImages.every(item => {
                return item.status == 2 || // 已通过/已下载
                    item.status == 3 || // 不通过
                    item.status == -1 // 已取消
            })
        }
        const availbleRowKeys = allRows
            .filter(item => selectRowKeys.includes(item.id))
            .filter(item => {
                const allImageSubmited = checkAllImageSubmited(item)
                const allImageMarked = checkAllImageMarked(item)
                if (!(!allImageSubmited && allImageMarked)) return false
                return true
            })
            .map(item => item.id)
        if (availbleRowKeys.length == 0) return message.warning('请选择可以提交反馈的批次')
        handleSubmit(availbleRowKeys, () => {
            setSelectRowKeys([])
            setIsMultiSelect(false)
        })
    }, [selectRowKeys, allRows, handleSubmit])
    const onSelect = useCallback((item) => {
        const key = item.id
        setSelectRowKeys(pre => {
            if (pre.includes(key)) {
                return pre.filter(item => item != key)
            } else {
                return [...pre, key]
            }
        })
    }, [])
    const handleDownload = useCallback((id) => {
        const selectRows = dataSource.flatMap(item => item.skuDeliveryList).filter(item => item.id == id)
        onDownload([id], selectRows, () => {
            setSelectRowKeys([])
            setIsMultiSelect(false)
        })
    }, [onDownload, dataSource])

    return (
        <div className={styles.DeliveryDetail}>
            <div className={styles.DeliveryDetailTitle}>
                <div>{version}</div>
                <DeliveryDetailActions
                    selectRowKeys={selectRowKeys}
                    isMultiSelect={isMultiSelect}
                    onDownload={onDownload}
                    onMultiDownload={onMultiDownload}
                    onMultiFeedback={onMultiFeedback}
                    onMultiSelect={onMultiSelect}
                    onSelectAll={onSelectAll}
                />
            </div>
            <div className={styles.DeliveryDetailDashboard}>
                <span>商品: {deliveryInfo?.skuNum || 0}个</span>
                <span>交付总数: {deliveryInfo?.deliveryNum || 0}张</span>
                <span>待验收: {deliveryInfo?.waitCheckNum || 0}张</span>
                {deliveryInfo?.acceptedNum ? <span>已通过: {deliveryInfo?.acceptedNum || 0}张</span> : null}
                {deliveryInfo?.processNum ? <span>不通过: {deliveryInfo?.processNum || 0}张</span> : null}
                {deliveryInfo?.cancelNum ? <span>已取消: {deliveryInfo?.cancelNum || 0}张</span> : null}
            </div>
            <div id="scrollableDiv" className={`${styles.DeliveryDetailContent} _beauty_scrollbar`}>
                <InfiniteScroll
                    dataLength={dataSource.length} //This is important field to render the next data
                    next={fetchData}
                    hasMore={hasMore}
                    loader={<h4>loading...</h4>}
                    scrollableTarget="scrollableDiv"
                    endMessage={
                        <p style={{ height: "50px", lineHeight: "50px", textAlign: 'center', color: '#cccccc' }}>
                            <span>没有更多内容了</span>
                        </p>
                    }
                    // below props only if you need pull down functionality
                    // refreshFunction={refreshData}
                    // pullDownToRefresh
                    // pullDownToRefreshThreshold={50}
                    // pullDownToRefreshContent={
                    //     <h3 style={{ textAlign: 'center' }}>&#8595; Pull down to refresh</h3>
                    // }
                    // releaseToRefreshContent={
                    //     <h3 style={{ textAlign: 'center' }}>&#8593; Release to refresh</h3>
                    // }
                >
                    <div className={styles.DeliveryItemList}>
                    {
                        dataSource.map(item => (
                            item.skuDeliveryList.map(item2 => (
                                <DeliveryItem
                                    key={item2.id}
                                    item={item2}
                                    onPreview={handlePreview}
                                    onSubmit={handleSubmit}
                                    onDownload={handleDownload}
                                    isMultiSelect={isMultiSelect}
                                    selectRowKeys={selectRowKeys}
                                    onSelect={onSelect}
                                />
                            ))
                        ))
                    }
                    </div>
                </InfiniteScroll>
            </div>
            <ImagePreviewWithLabel
                ref={previewRef}
                onLabel={handleLabel}
                onClose={onPreviewClose}
            />
        </div>
    )
})

export default DeliveryDetail